import { useEffect, useMemo, useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PageHeader, Panel } from "@/components/AppShell";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { usePortfolio } from "@/context/ProfileContext";
import { blendedReturn, contribOf, FUNDS, FUND_META, perPaycheck, perPaycheckLimit, project, taxDrag, tspLimit } from "@/lib/finance";
import { ContribInput } from "@/components/ContribInput";
import { usd, compact, changeClass } from "@/lib/format";
import { EarlyOutPanel } from "@/components/EarlyOutPanel";

function SliderRow({ label, value, display, min, max, step, onChange, testid }) {
  return (
    <div className="py-4 border-b border-border/60">
      <div className="flex justify-between text-sm mb-3"><span>{label}</span><span className="font-mono-num font-medium" data-testid={`${testid}-value`}>{display}</span></div>
      <Slider min={min} max={max} step={step} value={[value]} onValueChange={([v]) => onChange(v)} data-testid={testid} />
    </div>
  );
}

function ScenarioCard({ title, final, drag, rate, hero, testid }) {
  return (
    <div className={`glass p-5 ${hero ? "ring-1 ring-cyan-500/40" : ""}`} data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{title}</p>
      <p className={`font-mono-num text-2xl sm:text-3xl font-semibold mt-2 ${hero ? "text-grad-money" : "text-slate-900 dark:text-white"}`} data-testid={`${testid}-final`}>{usd(final)}</p>
      <dl className="mt-4 space-y-2 text-sm">
        <div className="flex justify-between"><dt className="text-muted-foreground">Est. tax drag on withdrawal</dt><dd className="font-mono-num text-rose-600 dark:text-rose-400" data-testid={`${testid}-drag`}>-{usd(drag)}</dd></div>
        <div className="flex justify-between"><dt className="text-muted-foreground">After-tax value</dt><dd className="font-mono-num font-medium" data-testid={`${testid}-net`}>{usd(final - drag)}</dd></div>
        <div className="flex justify-between"><dt className="text-muted-foreground">Blended return</dt><dd className="font-mono-num">{rate.toFixed(2)}%</dd></div>
      </dl>
    </div>
  );
}

export default function Simulator() {
  const { profile, dash } = usePortfolio();
  const [s, setS] = useState(null);
  useEffect(() => {
    if (profile) setS({ contrib: contribOf(profile), ageDelta: 0, alloc: { ...profile.allocation } });
  }, [profile]);
  const bal = dash?.totals?.total || 0;
  const r = useMemo(() => {
    if (!profile || !s) return null;
    const base = project(profile, bal);
    const hp = { ...profile, allocation: s.alloc };
    const hyp = project(hp, bal, { contrib: s.contrib, retireAge: profile.retire_age + s.ageDelta });
    const len = Math.max(base.series.length, hyp.series.length);
    const chart = [...Array(len)].map((_, i) => ({ age: profile.age + i, current: base.series[i]?.expected, hypothetical: hyp.series[i]?.expected }));
    return { base, hyp, chart, baseDrag: taxDrag(base.final.expected, profile, dash?.totals), hypDrag: taxDrag(hyp.final.expected, profile, dash?.totals) };
  }, [profile, s, bal, dash]);
  if (!r) return <p className="text-sm text-muted-foreground">Loading...</p>;
  const maxPct = Math.ceil((tspLimit(profile.age) / (profile.salary || 1)) * 1000) / 10;
  const fixed = s.contrib.mode === "fixed";
  const preset = (pct, cap) => setS({ ...s, contrib: { mode: s.contrib.mode, value: fixed ? Math[cap ? "floor" : "round"](cap ? perPaycheckLimit(profile) : perPaycheck(profile, pct)) : pct } });
  const allocTotal = FUNDS.reduce((t, f) => t + (s.alloc[f] || 0), 0);
  const delta = r.hyp.final.expected - r.base.final.expected;
  const minShift = Math.min(0, Math.max(40, Math.ceil(profile.age)) - profile.retire_age);
  return (
    <div>
      <PageHeader title="What-If Simulator" subtitle="Move the sliders. The terminal balance and chart recalculate instantly, nothing is saved until you update your profile." />
      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-6">
        <Panel title="Scenario levers" testid="panel-levers">
          <div className="py-4 border-b border-border/60"><ContribInput profile={profile} value={s.contrib} onChange={(c) => setS({ ...s, contrib: c })} testid="sim-contrib" /></div>
          <div className="flex gap-2 py-3">
            <Button size="sm" variant="outline" onClick={() => preset(5)} data-testid="sim-preset-match">Full match (5%)</Button>
            <Button size="sm" variant="outline" onClick={() => preset(maxPct, true)} data-testid="sim-preset-max">Max IRS limit ({fixed ? `$${Math.floor(perPaycheckLimit(profile)).toLocaleString()}` : `${maxPct}%`})</Button>
          </div>
          <SliderRow label="Retirement age shift" value={s.ageDelta} display={`${s.ageDelta >= 0 ? "+" : ""}${s.ageDelta} yrs → ${profile.retire_age + s.ageDelta}`} min={minShift} max={10} step={1} onChange={(v) => setS({ ...s, ageDelta: v })} testid="sim-age-slider" />
          <p className="text-sm font-medium mt-5 mb-1">Future contribution allocation</p>
          {FUNDS.map((f) => (
            <SliderRow key={f} label={<span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: FUND_META[f].color }} />{FUND_META[f].name}</span>} value={s.alloc[f] || 0} display={`${s.alloc[f] || 0}%`} min={0} max={100} step={5} onChange={(v) => setS({ ...s, alloc: { ...s.alloc, [f]: v } })} testid={`sim-alloc-${f}-slider`} />
          ))}
          <p className="text-xs text-muted-foreground mt-3 font-mono-num" data-testid="sim-alloc-total">Allocation total {allocTotal}% · blended {blendedReturn(s.alloc).toFixed(2)}%</p>
          <Button variant="ghost" size="sm" className="mt-3" onClick={() => setS({ contrib: contribOf(profile), ageDelta: 0, alloc: { ...profile.allocation } })} data-testid="sim-reset-button">Reset to current</Button>
        </Panel>
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <ScenarioCard title={`Current strategy · age ${profile.retire_age}`} final={r.base.final.expected} drag={r.baseDrag} rate={r.base.rate} testid="sim-current" />
            <ScenarioCard title={`Hypothetical · age ${profile.retire_age + s.ageDelta}`} final={r.hyp.final.expected} drag={r.hypDrag} rate={r.hyp.rate} hero testid="sim-hypothetical" />
          </div>
          <p className={`font-display text-lg font-semibold ${changeClass(delta)}`} data-testid="sim-delta">{delta >= 0 ? "+" : ""}{usd(delta)} difference in terminal balance</p>
          {["FERS", "CSRS"].includes(profile.retirement_system) && <EarlyOutPanel profile={profile} age={profile.retire_age + s.ageDelta} />}
          <Panel title="Terminal balance trajectory" testid="panel-sim-chart">
            <div className="h-[380px]" data-testid="sim-chart">
              <ResponsiveContainer>
                <LineChart data={r.chart}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
                  <XAxis dataKey="age" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                  <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} stroke="#94A3B8" width={64} />
                  <Tooltip formatter={(v) => usd(v)} labelFormatter={(l) => `Age ${l}`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line dataKey="current" name="Current strategy" stroke="#64748B" strokeWidth={2} dot={false} isAnimationActive={false} />
                  <Line dataKey="hypothetical" name="Hypothetical" stroke="#06B6D4" strokeWidth={2.5} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-muted-foreground mt-3">Returns use approximate long-run fund CAGRs (G 4.4%, F 5.2%, C 10.4%, S 9.3%, I 5.4%, L 7.4%). Past performance is not a guarantee. Tax drag applies your {profile.tax_rate}% assumed rate to the pre-tax share.</p>
          </Panel>
        </div>
      </div>
    </div>
  );
}
